<template>
    <pendo-popover-v2
        ref="userDropdownPopover"
        :title="$t('userDropdown.User profile')"
        placement="bottom-end"
        :hover="false"
        :popper-class="['user-dropdown__popover', 'top-nav__popover']"
        keyboard-navigation
        @show="focusFirstOption"
        @hide="$emit('select')">
        <template #default="{ shown, ...triggerAttrs }">
            <button
                v-pendo-tooltip="{
                    content: $t('userDropdown.User profile'),
                    arrow: true,
                }"
                v-bind="triggerAttrs"
                class="top-nav__trigger"
                :class="{ 'is-open': shown }"
                data-cy="user-dropdown-trigger"
                pendo-analytics="user-nav-item"
                :aria-label="$t('userDropdown.User profile')"
                @keydown.tab="handleTabKeydown($event, shown, triggerAttrs)">
                <user-icon :user="user" />
            </button>
        </template>
        <template #popover>
            <div
                ref="userContent"
                class="user-dropdown__content"
                data-cy="user-dropdown-content">
                <div class="user-dropdown__content-top">
                    <div class="user-dropdown__info">
                        <user-icon
                            class="user-dropdown__icon"
                            :user="user" />
                        <p
                            v-if="fullName"
                            ref="name"
                            v-pendo-tooltip="{
                                content: nameTooltipContent,
                                arrow: true,
                            }"
                            class="user-dropdown__name"
                            @mouseover="onMouseoverName">
                            {{ fullName }}
                        </p>
                        <p
                            ref="username"
                            v-pendo-tooltip="{
                                content: usernameTooltipContent,
                                arrow: true,
                            }"
                            class="user-dropdown__username"
                            :class="{ 'user-dropdown__username--primary': !fullName }"
                            @mouseover="onMouseoverUsername">
                            {{ user.username }}
                        </p>
                    </div>
                    <div
                        class="user-dropdown__menu-items">
                        <pendo-router-link
                            v-if="!isAutoProvisionedTrialExpired"
                            tabindex="0"
                            pendo-analytics="user-profile"
                            class="nav-option"
                            data-cy="user-profile-nav-link"
                            :to="{ name: 'UserProfile' }"
                            @click="handleProfileClick"
                            @keydown.space="handleProfileKeydown"
                            @keydown.enter="handleProfileKeydown"
                            @keydown.tab.prevent="handleProfileKeydown">
                            <user-profile-icon class="user-dropdown__option-icon" />
                            <div class="user-dropdown__option-label">
                                {{ $t('userDropdown.User profile') }}
                            </div>
                        </pendo-router-link>
                        <theme-popover
                            ref="themePopover"
                            @select="handleProfileSelect" />
                    </div>
                </div>
                <pendo-divider />
                <div class="user-dropdown__content-bottom">
                    <pendo-router-link
                        tabindex="0"
                        pendo-analytics="log-out"
                        class="nav-option"
                        @click="logOut"
                        @keydown.space.prevent="logOut"
                        @keydown.enter.prevent="logOut"
                        @keydown.tab.prevent="logOut">
                        <sign-out-icon class="user-dropdown__option-icon" />
                        <div class="user-dropdown__option-label">
                            {{ $t('userDropdown.Sign out') }}
                        </div>
                    </pendo-router-link>
                </div>
            </div>
        </template>
    </pendo-popover-v2>
</template>

<script>
import { PendoPopoverV2, PendoDivider, PendoTooltip } from '@pendo/components';
import UserIcon from '@/common/user/user-icon/user-icon.vue';
import PendoRouterLink from '@/vue-router/pendo-router-link.vue';
import SignOutIcon from '@/top-nav/components/icons/sign-out-icon.vue';
import UserProfileIcon from '@/top-nav/components/icons/user-profile-icon.vue';
import ThemePopover from '@/top-nav/components/theme-popover/theme-popover.vue';

import { mapState, mapGetters, mapActions } from 'vuex';
import escape from 'lodash/escape';
import { eventIsOpeningANewTabOrWindow } from '@/utils/window.utils.js';
import { elementHasOverflowX, getFirstFocusableNode } from '@/utils/dom.utils';
import { generateLoginQueryParams } from '@/utils/http.js';

export default {
    name: 'UserDropdown',
    components: {
        PendoPopoverV2,
        PendoDivider,
        UserIcon,
        PendoRouterLink,
        SignOutIcon,
        UserProfileIcon,
        ThemePopover,
    },
    directives: {
        PendoTooltip,
    },
    props: {
        preventTabKeyNavigation: {
            type: Boolean,
            default: false,
        },
    },
    emits: ['select'],
    data () {
        return {
            nameTooltipContent: null,
            usernameTooltipContent: null,
        };
    },
    computed: {
        ...mapState({
            user: (state) => state.auth.user,
        }),
        ...mapGetters({
            isAutoProvisionedTrialExpired: 'subscription/isAutoProvisionedTrialExpired',
        }),
        fullName () {
            if (!this.user.first || !this.user.last) {
                return null;
            }

            return escape(`${this.user.first} ${this.user.last}`).replace(/&#39;/g, "'");
        },
    },
    methods: {
        ...mapActions({
            logoutUser: 'auth/logout',
        }),
        async logOut () {
            const loginQueryString = generateLoginQueryParams().toString();
            await this.logoutUser();
            window.location.href = `/login?${loginQueryString}`;
        },
        hide () {
            this.$refs.userDropdownPopover?.hide?.();
            this.$refs.themePopover?.hidePopover?.();
        },
        onMouseoverName () {
            const { name } = this.$refs;

            const { clientWidth, scrollWidth } = name;
            const hasOverflow = elementHasOverflowX({ clientWidth, scrollWidth });

            if (hasOverflow) {
                this.nameTooltipContent = this.fullName;
            } else {
                this.nameTooltipContent = null;
            }
        },
        onMouseoverUsername () {
            const { username } = this.$refs;

            const { clientWidth, scrollWidth } = username;
            const hasOverflow = elementHasOverflowX({ clientWidth, scrollWidth });

            if (hasOverflow) {
                this.usernameTooltipContent = this.user.username;
            } else {
                this.usernameTooltipContent = null;
            }
        },
        handleProfileClick ($event) {
            if (eventIsOpeningANewTabOrWindow($event)) return;

            this.handleProfileSelect();
        },
        handleProfileKeydown ($event) {
            if (eventIsOpeningANewTabOrWindow($event)) return;

            $event.preventDefault();
            $event.currentTarget.click();
        },
        handleProfileSelect () {
            setTimeout(() => {
                this.hide();
                this.$emit('select');
            }, 0);
        },
        focusFirstOption () {
            setTimeout(() => {
                const firstFocusableNode = getFirstFocusableNode(this.$refs.userContent);
                if (firstFocusableNode) {
                    firstFocusableNode.focus();
                }
            }, 50);
        },
        handleTabKeydown ($event, shown, triggerAttrs) {
            if (this.preventTabKeyNavigation) {
                $event.preventDefault();

                if (shown) {
                    triggerAttrs.hide();
                } else {
                    triggerAttrs.show();
                }
            }
        },
    },
};
</script>

<style lang="less" scoped>
@import '@/side-nav/nav.style.less';

.top-nav__trigger {
    padding: 0px;
    border-radius: 50% !important;
}

.user-dropdown {
    &__content {
        display: flex;
        flex-direction: column;

        &-top {
            padding: 12px 12px 4px 12px;
            display: flex;
            gap: 8px;
            flex-direction: column;
        }

        &-bottom {
            padding: 4px 12px 12px 12px;
            display: flex;
            flex-direction: column;
            align-items: flex-start;
        }

        .nav-option {
            margin: 0;
            width: 100%;
        }
    }

    &__info {
        display: flex;
        flex-direction: column;
        align-items: center;
    }

    &__icon {
        margin: 16px 0;
        border-width: 1.5px;
        border-style: solid;
    }

    &__name {
        font-size: 14px;
        font-weight: 600;
        color: var(--palette-gray-100);
        margin-bottom: 2px;
        max-width: 100%;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    &__username {
        margin-bottom: 12px;
        padding: 0 12px;
        max-width: 100%;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        color: var(--palette-gray-70);
        box-sizing: border-box;

        &--primary {
            font-size: 14px;
            font-weight: 600;
            color: var(--palette-gray-100);
        }
    }

    &__menu-items {
        display: flex;
        flex-direction: column;
        gap: 2px;
        width: 100%;
    }

    &__option-icon {
        flex-shrink: 0;
    }
}

.pendo-divider {
    height: 12px !important;
}
</style>

<style lang="less">
.top-nav__popover.user-dropdown__popover {
    .pendo-popover-v2__panel {
        width: clamp(220px, 30vw, 240px);
        padding: 0;
    }
}
</style>
